import { forwardRef } from "react";

import { IconType } from "react-icons/lib";

import { Icon, Text } from "../../internal/exports";
import { Button } from "../../button/button";

export interface FooterIconProps {
  iconText?: string | number;
  width?: string | number;
  height?: string | number;
  iconAriaLabel?: string;
  testIdFooterIcon?: string;
  testIdCountText?: string;
  hideIconText?: boolean;
  icon?: IconType;
  onClick?(): any;
  isVoteActive?: boolean;
  isVoteDisabled?: boolean;
  isIconButtonPressed?: boolean;
}

export const FooterIcon = forwardRef<HTMLButtonElement, FooterIconProps>(
  (props, ref) => {
    const {
      iconText,
      icon,
      iconAriaLabel,
      width,
      height,
      onClick,
      hideIconText,
      testIdFooterIcon,
      testIdCountText,
      isVoteActive,
      isVoteDisabled,
      isIconButtonPressed,
      ...rest
    } = props;

    const hasIconText = iconText != null && iconText !== "";
    // Numeric counts inherit the button's md (1rem); label text (e.g. "Share")
    // stays xs, so we only drop an explicit fontSize for counts.
    const isNumericCount =
      typeof iconText === "number" ||
      (typeof iconText === "string" && /^\d+$/.test(iconText.trim()));

    return (
      <Button
        ref={ref}
        display="flex"
        variant="tertiary"
        justifyContent="center"
        onClick={onClick}
        h={4}
        gap={2}
        p={3}
        fontWeight="medium"
        // Drive the voted bg from state, not the recipe: a style prop sits in a
        // higher cascade layer and would always suppress the recipe's data-active bg.
        backgroundColor={isVoteActive ? "gray.200" : "transparent"}
        _hover={{ backgroundColor: isVoteActive ? "gray.200" : "gray.100" }}
        data-testid="card-footerButton"
        data-active={isVoteActive ? "" : undefined}
        aria-pressed={isIconButtonPressed ?? isVoteActive ?? undefined}
        aria-label={hideIconText || !hasIconText ? iconAriaLabel : undefined}
        disabled={isVoteDisabled}
        {...rest}
      >
        <Icon
          justifySelf="flex-start"
          w={width ? width : 4}
          h={height ? height : 4}
          as={icon}
          data-testid={testIdFooterIcon}
        />
        {hasIconText ? (
          <Text
            as="span"
            fontWeight="medium"
            fontSize={isNumericCount ? undefined : "xs"}
            hidden={hideIconText}
            data-testid={testIdCountText}
          >
            {iconText}
          </Text>
        ) : null}
      </Button>
    );
  }
);
FooterIcon.displayName = "FooterIcon";
